<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'LogicFlow',
})
</script>
<template>
  <section class="LogicFlow">
    <div id="flow"></div>
  </section>
</template>

<script lang="ts" setup name="LogicFlow">
import { ref, onMounted,onUnmounted, watch } from 'vue'
import icon from './icon/rectImg'
import customRect from './basic/customRect'
import { CustomCircle } from './basic/customRect'
import { CustomDiamond } from './basic/customRect'
import { CustomText } from './basic/customRect'

import { CustomBezier, CustomPolyline } from './edge/customBezierEdge'
import { selectedNodeStyle, nodeStyle, edgeStyle, selectedEdgeStyle, colors } from './utils/config'
import LogicFlow from '@logicflow/core'
import { Menu, DndPanel } from '@logicflow/extension'

// 最新版样式路径
import '@logicflow/core/lib/style/index.css'
import '@logicflow/extension/lib/style/index.css'
// 替换原来的导入方式
import type { NodeConfig, EdgeConfig } from '@logicflow/core/types'


let lfRef = ref<LogicFlow | null>(null)
let selectedNode = ref({ id: '', type: '', text: '' })
const currentTool = ref('select')
const currentEdgeType = ref('custom-polyline')
// 新增：存储复制的图形数据
const copiedElement = ref<NodeConfig | EdgeConfig | null>(null)

// 圆形图标 Base64
const circleIcon = `data:image/svg+xml;base64,${btoa(`
  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="18" fill="#e7f7fe" stroke="#90cffd" stroke-width="1"/>
  </svg>
`)}`;

const diamondIcon = `data:image/svg+xml;base64,${btoa(`
  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
    <polygon 
      points="20,5 35,20 20,35 5,20" 
      fill="#e7f7fe" 
      stroke="#90cffd" 
      stroke-width="1"
    />
  </svg>
`)}`;

// 文本图标 Base64
// 文本图标 Base64（修复编码问题）
// 在 LogicFlow 组件的 <script> 中定义文本图标（替换原错误的菱形图标）
const textIcon = `data:image/svg+xml;base64,${btoa('<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="10" width="30" height="20" fill="transparent" stroke="#90cffd" stroke-width="1"/><text x="20" y="25" font-size="12" text-anchor="middle" fill="#666">T</text></svg>')}`;

const emit = defineEmits(['parentNodes'])
const props = defineProps(['flowData'])

// 清除选中节点/连线样式
const clearSelectedNodeStyle = () => {
  const lf = lfRef.value
  if (!lf || !selectedNode.value.id) return

  if (selectedNode.value.type === 'node') {
    lf.setProperties(selectedNode.value.id, nodeStyle)
  } else {
    lf.setProperties(selectedNode.value.id, edgeStyle)
  }
  selectedNode.value = { id: '', type: '', text: '' }
}

// 获取流程图数据
const getFlowJson = () => {
  return lfRef.value?.getGraphData() || null
}

// 获取选中节点的所有父节点
const getAllParentNodes = (nodeId: string, nodeText: string) => {
  const lf = lfRef.value
  if (!lf) return []

  const parentNodes: string[] = [nodeText]
  const findParent = (id: string) => {
    const incomingNodes = lf.getNodeIncomingNode(id)
    const parentNode = incomingNodes?.[0]
    if (parentNode) {
      parentNodes.push(parentNode.text.value)
      findParent(parentNode.id)
    }
  }
  findParent(nodeId)
  emit('parentNodes', parentNodes)
  return parentNodes
}

// 连线工具 - 节点点击事件
let startNodeId = null
function handleEdgeNodeClick(e) {
  if (!startNodeId) {
    startNodeId = e.data.id;
  } else {
    // 加唯一id避免冲突
    const edgeId = `edge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const lf = lfRef.value;
    if (lf) {
      lf.addEdge({
        id: edgeId,
        sourceNodeId: startNodeId,
        targetNodeId: e.data.id,
        type: currentEdgeType.value
      });
      startNodeId = null; // 重置起点
    }
  }
}

// 工具切换核心方法
const switchTool = (toolType: 'select' | 'edge' | 'delete') => {
  const lf = lfRef.value;
  if (!lf) return;

  clearSelectedNodeStyle();
  startNodeId = null;
  lf.off('node:click', handleEdgeNodeClick);

  const graphModel = lf.graphModel;
  if (graphModel) {
    graphModel.editable = true;
    graphModel.canDrag = false;
    graphModel.canDelete = false;

    if (toolType === 'select') {
      graphModel.canDrag = true; // 允许拖拽
    } else if (toolType === 'delete') {
      graphModel.canDelete = true; // 允许删除
    } else if (toolType === 'edge') {
      lf.on('node:click', handleEdgeNodeClick); // 绑定连线事件
    }
  }

  currentTool.value = toolType;
};

// 切换连线类型
const switchEdgeType = (edgeType: 'custom-bezier' | 'custom-polyline') => {
  currentEdgeType.value = edgeType
  // 如果当前是连线工具，重新初始化连线模式
  if (currentTool.value === 'edge' && lfRef.value) {
    switchTool('edge')
  }
}

// 监听删除事件
const handleDelete = () => {
  selectedNode.value = { id: '', type: '', text: '' }
}

onMounted(() => {
  
  // 创建 LogicFlow 实例
  const lf = new LogicFlow({
    container: document.querySelector('#flow')!,
    plugins: [Menu, DndPanel],
    interactive: {
      node: {
        select: true,
        drag: false,
        delete: false
      },
      edge: {
        select: true
      }
    },
    snapline: false,
    panning: false,
    selecting: { enabled: true, multiple: false, rubberband: false }
  });

  // 注册节点和连线
  lf.register(customRect);
  lf.register(CustomCircle);
  lf.register(CustomDiamond);
  lf.register(CustomText);
  lf.register(CustomBezier);
  lf.register(CustomPolyline);
  lf.setDefaultEdgeType(currentEdgeType.value);

  lfRef.value = lf;

  // 节点点击事件（选择工具）
  lf.on('node:click', (event) => {
    const { id, text } = event.data || {};
    if (currentTool.value === 'select' && id) {
      clearSelectedNodeStyle();
      lf.setProperties(id, selectedNodeStyle);
      selectedNode.value = { id, type: 'node', text: text?.value || text || '' };
      getAllParentNodes(id, text?.value || text || '');
    }
  });

  // 连线点击事件
  lf.on('edge:click', (event) => {
    const { id, text } = event.data || {};
    if (currentTool.value === 'select' && id) {
      clearSelectedNodeStyle();
      lf.setProperties(id, selectedEdgeStyle);
      selectedNode.value = { id, type: 'edge', text: text?.value || text || '' };
    }
  });

  // 空白点击/删除事件
  lf.on('blank:click', clearSelectedNodeStyle);
  lf.on('node:delete', handleDelete);
  lf.on('edge:delete', handleDelete);

  // 主题配置
  lf.setTheme({
    bezier: {
      stroke: colors.edgeColor,
      adjustLine: { stroke: '#949494' },
      adjustAnchor: { r: 4, fill: '#949494', stroke: '#949494', fillOpacity: 1 }
    },
    polyline: {
      stroke: colors.edgeColor,
      strokeWidth: 1,
      offset: 20,
      adjustLine: { stroke: '#949494' },
      adjustAnchor: { r: 4, fill: '#949494', stroke: '#949494', fillOpacity: 1 }
    },
    nodeText: { fontSize: 13 },
    edgeText: { fontSize: 13 }
  });

  // 拖拽面板配置
  lf.extension.dndPanel.setPatternItems([
    { type: 'custom-rect', label: '方形', icon, className: 'flow-rect-node' },
    { type: 'custom-circle', label: '圆形', icon: circleIcon, className: 'flow-circle-node' ,properties: { r: 35} },
    { type: 'custom-diamond', label: '菱形', icon: diamondIcon, className: 'flow-diamond-node' ,properties: { width: 90, height: 90 } }
     ,{ type: 'custom-text', label: '文本', icon: textIcon, className: 'flow-text-node' }, // 新增文本节点
  ]);

  // 初始化工具为选择工具
  setTimeout(() => {
    switchTool('select');
  }, 0);

  // 监听 flowData 变化（导入数据）
  watch(
    () => props.flowData,
    (newVal) => {
      if (newVal && lfRef.value) {
        if (lfRef.value.importData) {
          lfRef.value.importData(newVal);
        } else if (lfRef.value.render) {
          lfRef.value.render(newVal);
        }
      }
    },
    { deep: true, immediate: true }
  );

  // 初始化工具为选择工具
  setTimeout(() => {
    switchTool('select');
  }, 0);

  // 新增：键盘事件处理（同时处理 Delete 和 Ctrl+C）
  const handleKeyDown = (e: KeyboardEvent) => {
    // 1. Delete 键删除功能（已有）
    if (e.key === 'Delete' && selectedNode.value.id) {
      const lf = lfRef.value;
      if (lf) {
        selectedNode.value.type === 'node' 
          ? lf.deleteNode(selectedNode.value.id)
          : lf.deleteEdge(selectedNode.value.id);
        clearSelectedNodeStyle();
      }
    }

    // 2. Ctrl+C 复制功能（新增）
// Ctrl+C 复制功能（使用正确类型）
    if (e.ctrlKey && e.key === 'c' && selectedNode.value.id) {
      e.preventDefault();
      const lf = lfRef.value;
      if (lf) {
        const graphData = lf.getGraphData();
        if (selectedNode.value.type === 'node') {
          // 复制节点（NodeConfig 类型）
          copiedElement.value = graphData.nodes.find(
            node => node.id === selectedNode.value.id
          ) || null;
        } else {
          // 复制连线（EdgeConfig 类型）
          copiedElement.value = graphData.edges.find(
            edge => edge.id === selectedNode.value.id
          ) || null;
        }
      }
    }

    // Ctrl+V 粘贴功能（修复文字错位）
    if (e.ctrlKey && e.key === 'v' && copiedElement.value) {
      e.preventDefault();
      const lf = lfRef.value;
      if (lf) {
        const newId = `copy_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const offset = 20; // 偏移量（可调整）
        
        if ('x' in copiedElement.value) { // 处理节点复制
          // 深拷贝节点数据（避免修改原数据）
          const node = JSON.parse(JSON.stringify(copiedElement.value));
          // 偏移节点本身的位置
          node.id = newId;
          node.x += offset;
          node.y += offset;
          
          // 关键：同步偏移文字的位置（如果有文字）
          if (node.text) {
            node.text.x = node.x; // 文字 x 与节点 x 保持一致
            node.text.y = node.y; // 文字 y 与节点 y 保持一致
            // （如果需要更精细的居中，可根据节点宽高微调，通常保持一致即可）
          }
          
          lf.addNode(node);
        } else if ('sourceNodeId' in copiedElement.value) { // 处理连线复制
          const edge = {
            ...copiedElement.value,
            id: newId,
          };
          lf.addEdge(edge);
        }
      }
    }
  };




  
  // 监听全局键盘按下事件
  window.addEventListener('keydown', handleKeyDown);

    
});

// 组件卸载时移除监听（避免内存泄漏）
  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });
  // =====================================================
</script>


<style lang="less" scoped>
.LogicFlow {
  height: 100vh;
  // padding-top: 60px;
  box-sizing: border-box;
  position: relative;

  .tool-bar {
    /* 工具栏样式不变 */
    position: absolute;
    top: 10px;
    left: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 100;

    button {
      padding: 6px 12px;
      border: 1px solid #dcdcdc;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #4aa9ff;
        color: #4aa9ff;
      }

      &.active {
        background: #4aa9ff;
        color: #fff;
        border-color: #4aa9ff;
      }
    }

    .edge-type-switch {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #666;
    }
  }

  // 关键修改：用 ::v-deep 穿透 scoped 作用域
  ::v-deep #flow {
    height: 100%;
    width: 100%;
    border: 1px solid #e8e8e8;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;

    /* 节点拖拽面板样式 */
    .flow-text-node {
      .lf-dnd-shape {
        background-size: 80% 80%;
        width: 60px;
        height: 40px;
        background-repeat: no-repeat;
        background-position: center;
      }
    }
    .flow-rect-node {
      .lf-dnd-shape {
        background-size: 80% 80%;
        width: 50px;
        height: 40px;
        // background-color: #e7f7fe;
        background-repeat: no-repeat;
        background-position: center;
      }
    }
    .flow-diamond-node {
      .lf-dnd-shape {
        background-size: 80% 80%;
        width: 40px;
        height: 40px;
        // background-color: #e7f7fe;
        background-repeat: no-repeat;
        background-position: center;
      }
      rect {
        opacity: 0;
        width: 100%;
        height: 100%;
      }
    }
    .flow-circle-node {
      .lf-dnd-shape {
        background-size: 80% 80%;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        // background-color: #e7f7fe;
        background-repeat: no-repeat;
        background-position: center;
      }
    }

    rect {
      cursor: pointer;
    }
  }
}
::v-deep svg {
  background-color: #fafafa !important;
  background-image: 
    // linear-gradient(to right, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
    // linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
    radial-gradient(circle, rgba(0, 0, 0, 0.2) 1px, transparent 1px) !important;
  background-size: 15px 15px !important;
  background-position: 0 0 !important;
  position: relative !important;
  z-index: 1 !important;
}

:deep(.lf-node-type-custom-text:not(.lf-selected):not(.lf-editing)) {
  stroke: transparent !important;
  fill: transparent !important;
}

:deep(.lf-node-type-custom-text:not(.lf-selected):not(.lf-editing) text) {
  fill: #000 !important;
}

:deep(.lf-node-type-custom-text.lf-selected) {
  stroke: #4aa9ff !important;
  fill: #f0f8ff !important;
  stroke-width: 1 !important;
}

:deep(.lf-node-type-custom-text.lf-editing) {
  stroke: #4aa9ff !important;
  fill: #f0f8ff !important;
  stroke-width: 1 !important;
}


</style>